import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  Grid,
  IconButton,
  Input,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import {
  CalendarIcon,
  ContractAIWhiteIcon,
  ContractDateIcon,
  ContractNotesIcon,
  ContractPartiesIcon,
  ContractProjectIcon,
  ContractRevisionIcon,
  ContractTitleIcon,
  PDFImage,
} from '../../assets/icons/icons'
import { useEffect, useState } from 'react'
// @ts-ignore
import Lottie from 'react-lottie'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { ExpandMoreOutlined } from '@mui/icons-material'
import { useGetProjectsAll } from '../projects/hooks'
import moment from 'moment'
import SelectTagsComponent from '../projects/uploads/SelectTagsComponent'
import successAnimation from '../../assets/lotties/success_lottie.json'
import { useAddContract } from './hooks'

const ContractForm = ({
  selectedFiles,
  setCurrentState,
  formatFileSize,

  title,
  setTitle,
  date,
  setDate,
  lastRevision,
  setLastRevision,
  parties,
  setParties,
  selectedProjects,
  setSelectedProjects,
  notes,
  setNotes,
  summary,
  setSummary,
  mutationResponse,
  setMutationResponse,
}: {
  selectedFiles: any
  setCurrentState: any
  formatFileSize: any
  title: string
  setTitle: (title: string) => void
  date: any
  setDate: (date: any) => void
  lastRevision: any
  setLastRevision: (lastRevision: any) => void
  parties: string[]
  setParties: (parties: string[]) => void
  selectedProjects: any
  setSelectedProjects: any
  notes?: any
  setNotes?: any
  summary?: any
  setSummary?: any
  mutationResponse?: any
  setMutationResponse?: any
}) => {
  interface ContractAiPayload {
    title: string
    date?: string
    lastRevision?: string
    parties: string[]
    summary: string
    notes: string
    project?: string
  }
  const [originalTitle, setOriginalTitle] = useState('')
  const [originalDate, setOriginalDate] = useState<any>(null)
  const [originalSelectedProjects, setOriginalSelectedProjects] =
    useState<any>(null)
  const [originalLastRevision, setOriginalLastRevision] = useState('')
  const [originalParties, setOriginalParties] = useState<string[]>([])
  // Use Theme
  const theme = useTheme()

  // Use States
  const [isEditMode, setIsEditMode] = useState(false)
  const [isLottie, setIsLottie] = useState(false)
  useEffect(() => {
    setOriginalTitle(title)
    setOriginalDate(date)
    setOriginalSelectedProjects(selectedProjects)
    setOriginalParties(parties.slice())
  }, [isEditMode])

  // All Projects
  const { data: allProjects } = useGetProjectsAll('projects')

  const { mutate: mutateUpdate, isLoading: isLoading } =
    useAddContract('add-contract')

  // Handle Save
  const handleSave = () => {
    setIsEditMode(false)
  }

  const handleCancel = () => {
    setTitle(originalTitle)
    setDate(originalDate)
    setSelectedProjects(originalSelectedProjects)
    setLastRevision(originalLastRevision)
    setParties(originalParties.slice())
    setIsEditMode(false)
  }

  // Filter the Project ID
  const findProjectIdByName = (projects: any, selectedName: any) => {
    const project = projects?.find((p: any) => p?.projectName === selectedName)
    return project ? project?.id : null
  }

  // Handle Process with AI
  const handleProcessWithAI = () => {
    const projectId = findProjectIdByName(allProjects, selectedProjects)

    const payload: ContractAiPayload = {
      title: title,
      parties: parties?.map(party => party?.trim()),
      notes: notes,
      summary: summary,
    }

    if (projectId) {
      payload.project = projectId
    }

    if (date) {
      const parsedDate = new Date(date)
      if (!isNaN(parsedDate?.getTime())) {
        payload.date = parsedDate?.toISOString()
      }
    }

    if (lastRevision) {
      const parsedLastRevision = new Date(lastRevision)
      if (!isNaN(parsedLastRevision?.getTime())) {
        payload.lastRevision = parsedLastRevision?.toISOString()
      }
    }

    setIsLottie(true)
    mutateUpdate(payload, {
      onSuccess: data => {
        setMutationResponse(data)
        setTimeout(() => {
          setIsLottie(false)
          setCurrentState('detail')
        }, 2000)
      },
      onError: () => {
        setTimeout(() => {
          setIsLottie(false)
        }, 2000)
      },
    })
  }

  // Lottie
  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: successAnimation,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  }

  return (
    <>
      {isLottie ? (
        <Card
          sx={{
            padding: 1,
            boxShadow: 'none',
            border: '1px solid #EEEEEE',
            borderRadius: '10px',
            minHeight: '70vh',
            pb: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <Lottie options={defaultOptions} height={400} width={400} />
        </Card>
      ) : (
        <Card
          sx={{
            padding: 1,
            boxShadow: 'none',
            border: '1px solid #EEEEEE',
            borderRadius: '10px',
            minHeight: '70vh',
            pb: 2,
          }}>
          <Box
            sx={{
              paddingBlock: 2,
              paddingInline: 1,
            }}>
            <Typography
              sx={{
                color: '#5F5C5F',
                mb: 0.5,
                fontWeight: 'semibold',
              }}>
              Verify the details below, then submit to our AI for processing.
            </Typography>
          </Box>

          <Box
            sx={{
              backgroundColor: '#f7f5f9',
              p: 3,
              borderRadius: '7px',
              maxWidth: '70%',
            }}>
            <Grid container spacing={4}>
              <Grid item md={4}>
                <Box>
                  <PDFImage />
                  {selectedFiles && selectedFiles?.length > 0 && (
                    <>
                      <Typography
                        sx={{
                          color: '#2B262C',
                          overflowWrap: 'anywhere',
                          mr: 2,
                        }}>
                        {selectedFiles[0]?.name}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#5A525B80',
                          fontSize: '14px',
                          mt: 1,
                        }}>
                        PDF {formatFileSize(selectedFiles[0]?.size) + 'kb'}
                      </Typography>
                    </>
                  )}
                </Box>
              </Grid>

              {isEditMode ? (
                <Grid item md={8}>
                  <Typography
                    fontSize={'14px'}
                    color='#5A525B'
                    paddingBottom={0.5}
                    fontWeight='bold'>
                    Title
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #DBDBDB',
                      padding: theme.spacing(0.8, 1.6, 0.8, 1.6),
                    }}
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}>
                    <Input
                      disableUnderline
                      sx={{
                        width: '100%',
                        fontSize: '17px',
                        '& input::placeholder': {
                          fontWeight: 'bold',
                          textOverflow: "ellipsis",
                        },
                      }}
                      type='text'
                      fullWidth={true}
                      onChange={(e: any) => setTitle(e.target.value)}
                      value={title}
                      required={true}
                      disabled={false}
                      multiline
                    />
                  </Box>

                  <Typography
                    fontSize={'14px'}
                    color='#5A525B'
                    paddingBottom={0.5}
                    fontWeight='bold'
                    marginTop='15px'>
                    Date
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #DBDBDB',
                      padding: theme.spacing(0.8, 1.6, 0.8, 1.6),
                      zIndex: '88',
                    }}
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={date}
                        onChange={(e: any) => setDate(e)}
                        inputFormat='MM DD, YYYY'
                        renderInput={(params: any) => {
                          return (
                            <TextField
                              {...params}
                              variant='standard'
                              name='projectDueDate'
                              label=''
                              required
                              InputProps={{
                                ...params?.InputProps,
                                endAdornment: (
                                  // @ts-ignore
                                  <IconButton
                                    {...params?.InputProps}
                                    onClick={event => {
                                      // @ts-ignore
                                      params?.InputProps?.endAdornment?.props?.children?.props?.onClick()
                                    }}>
                                    <CalendarIcon />
                                  </IconButton>
                                ),
                              }}
                              sx={{
                                width: '100%',
                                '& .MuiInput-underline:before': {
                                  display: 'none',
                                },
                                '& .MuiInput-underline:hover:not(.Mui-disabled):before':
                                  {
                                    display: 'none',
                                  },
                                '& .MuiInput-underline:after': {
                                  display: 'none',
                                },
                              }}
                            />
                          )
                        }}
                      />
                    </LocalizationProvider>
                  </Box>

                  <Typography
                    fontSize={'14px'}
                    color='#5A525B'
                    paddingBottom={0.5}
                    fontWeight='bold'
                    marginTop='15px'>
                    Project
                  </Typography>
                  <FormControl
                    sx={{
                      width: '100%',
                      fontSize: '13px',
                      color: '#ACB9B5',
                    }}>
                    <Box
                      sx={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        border: '1px solid #DBDBDB',
                      }}
                      display={'flex'}
                      alignItems={'center'}
                      justifyContent={'center'}>
                      <Select
                        id='demo-simple-select'
                        value={selectedProjects}
                        onChange={e => {
                          setSelectedProjects(e.target.value)
                        }}
                        IconComponent={ExpandMoreOutlined}
                        sx={{
                          width: '100%',
                          border: 'none',
                          boxShadow: 'none',
                          padding: theme.spacing(0.8, 1.6, 0.8, 1.6),
                          '& .MuiSelect-select': {
                            padding: theme.spacing(0.8, 0, 0.8, 0),
                          },
                          '&:hover, & fieldset, & fieldset:hover': {
                            border: 'none',

                            margin: 0,
                            boxShadow: 'none',
                          },
                        }}
                        displayEmpty>
                        {allProjects?.map((item: any, index: number) => (
                          <MenuItem value={item?.projectName}>
                            {item?.projectName}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  </FormControl>

                  <Typography
                    fontSize='14px'
                    color='#5A525B'
                    paddingBottom={0.5}
                    fontWeight='bold'
                    marginTop='15px'>
                    Parties
                  </Typography>
                  <SelectTagsComponent
                    tags={parties}
                    setTags={setParties}
                    width='37%'
                  />

                  <Typography
                    fontSize={'14px'}
                    color='#5A525B'
                    paddingBottom={0.5}
                    fontWeight='bold'
                    marginTop='15px'>
                    Notes
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '1px solid #DBDBDB',
                      padding: theme.spacing(1, 1.6, 1, 1.6),
                    }}
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}>
                    <Input
                      disableUnderline
                      multiline
                      sx={{
                        width: '100%',
                        fontSize: '17px',
                        '& input::placeholder': {
                          fontWeight: 'bold',
                        },
                      }}
                      name={'projectDescription'}
                      type='text'
                      rows={3}
                      fullWidth={true}
                      onChange={(e: any) => setNotes(e.target.value)}
                      value={notes}
                      required={true}
                      disabled={false}
                    />
                  </Box>

                  <Box
                    sx={{
                      mt: 4,
                      width: '100%',
                      display:"flex",
                      alignItems:"center",
                      
                    }}>
                    <Button
                      variant='contained'
                      sx={{
                        color: '#24212C',
                        background: '#DCDAE1',
                        fontSize: '13px',
                        textTransform: 'none',
                        py: 1.8,
                        width: '22%',
                        boxShadow: 'none',
                        ':hover': {
                          background: '#DCDAE1',
                          boxShadow: 'none',
                        },
                        borderRadius: 1.8,
                        mr: 3,
                      }}
                      onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button
                      variant='contained'
                      sx={{
                        color: '#FFF',
                        background: '#F0845D',
                        fontSize: '13px',
                        textTransform: 'none',
                        py: 1.8,
                        width: '73%',
                        boxShadow: 'none',
                        ':hover': {
                          background: '#F0845D',
                          boxShadow: 'none',
                        },
                        borderRadius: 1.8,
                      }}
                      onClick={handleSave}>
                      Save
                    </Button>
                  </Box>
                </Grid>
              ) : (
                <>
                  <Grid item md={3}>
                    <Typography
                      sx={{
                        color: '#938994',
                        borderBottom: '1px solid #DBDBDB',
                        pb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                      }}>
                      <ContractTitleIcon /> Title
                    </Typography>
                    <Typography
                      sx={{
                        color: '#938994',
                        borderBottom: '1px solid #DBDBDB',
                        pb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                      }}>
                      <ContractDateIcon /> Date
                    </Typography>
                    <Typography
                      sx={{
                        color: '#938994',
                        borderBottom: '1px solid #DBDBDB',
                        pb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                      }}>
                      <ContractRevisionIcon /> Last Revision
                    </Typography>
                    <Typography
                      sx={{
                        color: '#938994',
                        borderBottom: '1px solid #DBDBDB',
                        pb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                      }}>
                      <ContractProjectIcon /> Project
                    </Typography>
                    <Typography
                      sx={{
                        color: '#938994',
                        borderBottom: '1px solid #DBDBDB',
                        pb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                      }}>
                      <ContractPartiesIcon /> Parties
                    </Typography>
                    <Typography
                      sx={{
                        color: '#938994',
                        pb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                      <ContractNotesIcon /> Notes
                    </Typography>
                  </Grid>

                  <Grid item md={5}>
                    <Typography
                      sx={{
                        color: '#938994',
                        borderBottom: '1px solid #DBDBDB',
                        pb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                      }}>
                      <Tooltip title={title || "---"}>
                        <Box
                        style={{
                          display: 'inline-block',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {title && title.length > 40 ? `${title.slice(0, 40)}...` : title || "---"}
                      </Box>
                    </Tooltip>
                    </Typography>
                    <Typography
                      sx={{
                        color: '#938994',
                        borderBottom: '1px solid #DBDBDB',
                        pb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                      }}>
                      {(date &&
                        moment(
                          typeof date === 'string' ? date : date?.toDate()
                        ).isValid() &&
                        moment(
                          typeof date === 'string' ? date : date?.toDate()
                        ).format('MMM DD, YYYY')) ||
                        '---'}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#938994',
                        borderBottom: '1px solid #DBDBDB',
                        pb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                      }}>
                      {lastRevision || '---'}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#938994',
                        borderBottom: '1px solid #DBDBDB',
                        pb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                      }}>
                      {allProjects?.find(
                        (item: any) => item?.projectName === selectedProjects
                      )?.projectName || '---'}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#938994',
                        borderBottom: '1px solid #DBDBDB',
                        pb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                      }}>
                      {parties?.join(', ') || '---'}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#938994',
                        pb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                      {notes || '---'}
                    </Typography>
                    <Button
                      variant='contained'
                      sx={{
                        color: '#7C7A80',
                        background: '#fff',
                        fontSize: '13px',
                        textTransform: 'none',
                        py: 0.6,
                        width: '7.5rem',
                        boxShadow: 'none',
                        ':hover': {
                          background: '#fff',
                          boxShadow: '1px',
                        },
                        borderRadius: 1.8,
                        border: '1px solid #7C7A80',
                        mr: 3,
                      }}
                      onClick={() => setIsEditMode(true)}>
                      Edit Details
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          {!isEditMode && (
            <Box
              sx={{
                mt: 4,
              }}>
              <Button
                variant='contained'
                sx={{
                  color: '#24212C',
                  background: '#DCDAE1',
                  fontSize: '13px',
                  textTransform: 'none',
                  py: 1.8,
                  width: '7rem',
                  boxShadow: 'none',
                  ':hover': {
                    background: '#DCDAE1',
                    boxShadow: '1px',
                  },
                  borderRadius: 1.8,
                  mr: 3,
                }}
                onClick={() => setCurrentState('upload')}>
                Go Back
              </Button>
              <Button
                variant='contained'
                sx={{
                  color: '#FFF',
                  background: '#F0845D',
                  fontSize: '13px',
                  textTransform: 'none',
                  py: 1.8,
                  width: '17rem',
                  boxShadow: 'none',
                  ':hover': {
                    background: '#F0845D',
                    boxShadow: '1px',
                  },
                  borderRadius: 1.8,
                }}
                onClick={handleProcessWithAI}>
                <ContractAIWhiteIcon />
                <span style={{ marginLeft: '8px' }}>Process with AI</span>
              </Button>
            </Box>
          )}
        </Card>
      )}
    </>
  )
}

export default ContractForm
